import React from 'react';
import { DeckCard } from '../../types/pokemon';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { X } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';

interface DeckCardListProps {
  cards: DeckCard[];
  onRemoveCard?: (cardId: number) => void;
  showRemoveButton?: boolean;
  showLargeCards?: boolean;
}

export function DeckCardList({
  cards,
  onRemoveCard,
  showRemoveButton = true,
  showLargeCards = false
}: DeckCardListProps) {
  const { lang, t } = useI18n();
  
  const sortedCards = [...cards].sort((a, b) => {
    const rarityOrder = {
      '♛': 1,
      '☆☆☆': 2,
      '☆☆': 3,
      '☆': 4,
      '♢♢♢♢': 5,
      '♢♢♢': 6,
      '♢♢': 7,
      '♢': 8
    };
    
    const rarityA = rarityOrder[a.card?.rarity as keyof typeof rarityOrder] || 999;
    const rarityB = rarityOrder[b.card?.rarity as keyof typeof rarityOrder] || 999;
    
    if (rarityA !== rarityB) return rarityA - rarityB;
    
    // Utiliser le nom de carte en fonction de la langue
    const nameA = lang === 'en' ? (a.card?.card_en || a.card?.card_fr || '') : (a.card?.card_fr || a.card?.card_en || '');
    const nameB = lang === 'en' ? (b.card?.card_en || b.card?.card_fr || '') : (b.card?.card_fr || b.card?.card_en || '');
    return nameA.localeCompare(nameB);
  });

  if (showLargeCards) {
    return (
      <>
        {sortedCards.map((deckCard) => {
          const card = deckCard.card;
          if (!card) return null;

          // Pour l'anglais, placer "en/" avant "preview/"
          const imagePath = card.image 
            ? `${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/preview/' : 'preview/'}${card.image}` 
            : null;
          
          // Utiliser le nom de carte en fonction de la langue
          const cardName = lang === 'en' ? card.card_en || card.card_fr : card.card_fr || card.card_en;

          return (
            <div key={card.id} className="relative group">
              {imagePath && (
                <div className="aspect-[3/4] relative">
                  <img
                    src={imagePath}
                    alt={cardName || t('common.pokemon_card')}
                    className="w-full h-full object-contain rounded-lg transition-transform duration-200 group-hover:scale-105"
                  />
                  {deckCard.quantity > 1 && (
                    <div className="absolute top-2 right-2 bg-indigo-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                      ×{deckCard.quantity}
                    </div>
                  )}
                  {showRemoveButton && onRemoveCard && (
                    <button
                      onClick={() => onRemoveCard(card.id)}
                      className="absolute top-2 left-2 p-1 bg-red-100 text-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-200"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  }

  return (
    <div className="space-y-4">
      {sortedCards.map((deckCard) => {
        const card = deckCard.card;
        if (!card) return null;

        // Pour l'anglais, placer "en/" avant "preview/"
        const imagePath = card.image 
          ? `${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/preview/' : 'preview/'}${card.image}` 
          : null;
        
        // Utiliser le nom de carte en fonction de la langue
        const cardName = lang === 'en' ? card.card_en || card.card_fr : card.card_fr || card.card_en;

        return (
          <div
            key={card.id}
            className="flex items-center gap-4 bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow"
          >
            {imagePath && (
              <img
                src={imagePath}
                alt={cardName || t('common.pokemon_card')}
                className="w-16 h-16 object-contain"
              />
            )}
            
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 truncate">
                {cardName}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-gray-500">{card.set}</span>
                <span className="px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
                  {card.rarity}
                </span>
                <span className="text-sm text-gray-500">
                  × {deckCard.quantity}
                </span>
              </div>
            </div>

            {showRemoveButton && onRemoveCard && (
              <button
                onClick={() => onRemoveCard(card.id)}
                className="p-1 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}